import { useCallback, useEffect, useState } from "react";
import {
  ChevronDown, ChevronRight, Plus, Trash2, BookOpen, GraduationCap, FileText,
  Users, Award, BarChart3,
} from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const TIERS = ["free", "starter", "growth", "enterprise"];
const STATUSES = ["draft", "published", "archived"];
const LEVELS = ["Beginner", "Intermediate", "Advanced"];
const STAGES = ["idea", "formation", "banking", "credit", "funding", "growth", "scale"];

function StatusPill({ value, onChange, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="text-[11px] bg-[#0d0d0f] border border-white/10 rounded-full px-2 py-1 text-[#A1A1AA] focus:outline-none focus:border-[#D4AF37]/40">
      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
}

function LessonEditor({ courseId, onDone }) {
  const [title, setTitle] = useState("");
  const [minutes, setMinutes] = useState(8);
  const [body, setBody] = useState("");
  const [video, setVideo] = useState("");
  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    try {
      await api.post("/lms-admin/lessons", { course_id: courseId, title, minutes: Number(minutes), body, video: video || null, type: video ? "video" : "reading", status: "published" });
      toast.success("Lesson created"); onDone();
    } catch { toast.error("Could not create lesson"); }
  };
  return (
    <div className="rounded-lg border border-white/10 bg-[#0d0d0f] p-3 space-y-2 mt-2" data-testid="admin-lesson-editor">
      <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-lesson-title" placeholder="Lesson title" className="w-full bg-[#121214] border border-white/10 rounded px-2 h-8 text-sm text-white" />
      <div className="flex gap-2">
        <input value={minutes} onChange={(e) => setMinutes(e.target.value)} type="number" placeholder="Minutes" className="w-24 bg-[#121214] border border-white/10 rounded px-2 h-8 text-sm text-white" />
        <input value={video} onChange={(e) => setVideo(e.target.value)} placeholder="YouTube ID or video URL (optional)" className="flex-1 bg-[#121214] border border-white/10 rounded px-2 h-8 text-sm text-white" />
      </div>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Lesson content…" className="w-full h-20 bg-[#121214] border border-white/10 rounded p-2 text-sm text-white resize-none" />
      <Button onClick={save} data-testid="admin-lesson-save" size="sm" className="fv-gold-gradient text-[#0A0A0B] rounded-full">Add lesson</Button>
    </div>
  );
}

function CourseRow({ course, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [lessons, setLessons] = useState([]);
  const [adding, setAdding] = useState(false);
  const loadLessons = useCallback(() => api.get(`/lms-admin/courses/${course.course_id}/lessons`).then(({ data }) => setLessons(data)).catch(() => {}), [course.course_id]);
  useEffect(() => { if (open) loadLessons(); }, [open, loadLessons]);

  const setStatus = async (status) => { await api.patch(`/lms-admin/courses/${course.course_id}`, { status }); toast.success(`Course ${status}`); onRefresh(); };
  const del = async () => { if (!window.confirm(`Delete course "${course.title}" and its lessons?`)) return; await api.delete(`/lms-admin/courses/${course.course_id}`); toast.success("Course deleted"); onRefresh(); };
  const delLesson = async (id) => { await api.delete(`/lms-admin/lessons/${id}`); loadLessons(); };
  const setLessonStatus = async (id, status) => { await api.patch(`/lms-admin/lessons/${id}`, { status }); loadLessons(); };

  return (
    <div className="rounded-lg border border-white/8 bg-[#121214] ml-4 mt-2">
      <div className="flex items-center gap-2 p-3">
        <button onClick={() => setOpen(!open)} data-testid={`admin-course-toggle-${course.course_id}`} className="text-[#71717A]">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
        <BookOpen className="w-4 h-4 text-[#D4AF37]" />
        <span className="text-sm text-white flex-1">{course.title}</span>
        <span className="text-[11px] text-[#71717A]">{course.lessons_count} lessons · {course.tier}</span>
        <StatusPill value={course.status} onChange={setStatus} testid={`admin-course-status-${course.course_id}`} />
        <button onClick={del} data-testid={`admin-course-delete-${course.course_id}`} className="text-[#71717A] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      {open && (
        <div className="px-4 pb-3">
          {lessons.map((l) => (
            <div key={l.lesson_id} className="flex items-center gap-2 py-1.5 border-t border-white/5" data-testid={`admin-lesson-${l.lesson_id}`}>
              <FileText className="w-3.5 h-3.5 text-[#71717A]" />
              <span className="text-sm text-[#D4D4D8] flex-1">{l.title}</span>
              <StatusPill value={l.status} onChange={(s) => setLessonStatus(l.lesson_id, s)} testid={`admin-lesson-status-${l.lesson_id}`} />
              <button onClick={() => delLesson(l.lesson_id)} className="text-[#71717A] hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {adding ? <LessonEditor courseId={course.course_id} onDone={() => { setAdding(false); loadLessons(); onRefresh(); }} />
            : <button onClick={() => setAdding(true)} data-testid={`admin-add-lesson-${course.course_id}`} className="mt-2 text-xs text-[#D4AF37] flex items-center gap-1"><Plus className="w-3 h-3" /> Add lesson</button>}
        </div>
      )}
    </div>
  );
}

function CourseCreator({ pathId, onDone }) {
  const [title, setTitle] = useState("");
  const [tier, setTier] = useState("starter");
  const [level, setLevel] = useState("Beginner");
  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    await api.post("/lms-admin/courses", { path_id: pathId, title, tier, level, status: "published" });
    toast.success("Course created"); onDone();
  };
  return (
    <div className="ml-4 mt-2 rounded-lg border border-white/10 bg-[#0d0d0f] p-3 flex gap-2 flex-wrap" data-testid="admin-course-creator">
      <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-course-title" placeholder="Course title" className="flex-1 min-w-40 bg-[#121214] border border-white/10 rounded px-2 h-8 text-sm text-white" />
      <select value={level} onChange={(e) => setLevel(e.target.value)} className="bg-[#121214] border border-white/10 rounded px-2 h-8 text-sm text-white">{LEVELS.map((l) => <option key={l}>{l}</option>)}</select>
      <select value={tier} onChange={(e) => setTier(e.target.value)} className="bg-[#121214] border border-white/10 rounded px-2 h-8 text-sm text-white">{TIERS.map((t) => <option key={t}>{t}</option>)}</select>
      <Button onClick={save} data-testid="admin-course-save" size="sm" className="fv-gold-gradient text-[#0A0A0B] rounded-full">Add</Button>
    </div>
  );
}

function PathRow({ path, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const setStatus = async (status) => { await api.patch(`/lms-admin/paths/${path.path_id}`, { status }); toast.success(`Path ${status}`); onRefresh(); };
  const del = async () => { if (!window.confirm(`Delete path "${path.title}" and all its courses/lessons?`)) return; await api.delete(`/lms-admin/paths/${path.path_id}`); toast.success("Path deleted"); onRefresh(); };
  return (
    <div className="rounded-xl border border-white/8 bg-[#0d0d0f] p-1" data-testid={`admin-path-${path.path_id}`}>
      <div className="flex items-center gap-2 p-3">
        <button onClick={() => setOpen(!open)} data-testid={`admin-path-toggle-${path.path_id}`} className="text-[#71717A]">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
        <GraduationCap className="w-4 h-4 text-[#D4AF37]" />
        <span className="text-sm text-white font-medium flex-1">{path.title}</span>
        <span className="text-[11px] text-[#71717A] capitalize">{path.stage} · {path.courses_count} courses</span>
        <StatusPill value={path.status} onChange={setStatus} testid={`admin-path-status-${path.path_id}`} />
        <button onClick={del} data-testid={`admin-path-delete-${path.path_id}`} className="text-[#71717A] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
      {open && (
        <div className="pb-2">
          {path.courses.map((c) => <CourseRow key={c.course_id} course={c} onRefresh={onRefresh} />)}
          {creating ? <CourseCreator pathId={path.path_id} onDone={() => { setCreating(false); onRefresh(); }} />
            : <button onClick={() => setCreating(true)} data-testid={`admin-add-course-${path.path_id}`} className="ml-4 mt-2 text-xs text-[#D4AF37] flex items-center gap-1"><Plus className="w-3 h-3" /> Add course</button>}
        </div>
      )}
    </div>
  );
}

function PathCreator({ onDone }) {
  const [title, setTitle] = useState("");
  const [stage, setStage] = useState("growth");
  const [tier, setTier] = useState("starter");
  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    await api.post("/lms-admin/paths", { title, stage, tier, status: "published", description: title });
    toast.success("Path created"); setTitle(""); onDone();
  };
  return (
    <div className="rounded-xl border border-[#D4AF37]/20 bg-[#151310] p-3 mb-4 flex gap-2 flex-wrap" data-testid="admin-path-creator">
      <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-path-title" placeholder="New learning path title" className="flex-1 min-w-48 bg-[#121214] border border-white/10 rounded px-3 h-9 text-sm text-white" />
      <select value={stage} onChange={(e) => setStage(e.target.value)} className="bg-[#121214] border border-white/10 rounded px-2 h-9 text-sm text-white capitalize">{STAGES.map((s) => <option key={s}>{s}</option>)}</select>
      <select value={tier} onChange={(e) => setTier(e.target.value)} className="bg-[#121214] border border-white/10 rounded px-2 h-9 text-sm text-white">{TIERS.map((t) => <option key={t}>{t}</option>)}</select>
      <Button onClick={save} data-testid="admin-path-save" className="fv-gold-gradient text-[#0A0A0B] rounded-full"><Plus className="w-4 h-4 mr-1" /> Create path</Button>
    </div>
  );
}

export default function AdminAcademy() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const load = useCallback(() => {
    api.get("/lms-admin/overview").then(({ data }) => setData(data)).catch(() => {});
    api.get("/lms-admin/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <p className="text-[#71717A]">Loading Academy CMS…</p>;

  return (
    <div data-testid="admin-academy">
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[[Users, "Students", analytics.total_students], [BarChart3, "Quiz avg", `${analytics.quiz_avg}%`],
            [Award, "Certificates", analytics.certificates_issued], [FileText, "Lessons", data.totals.lessons]].map(([Icon, label, val], i) => (
            <div key={i} className="rounded-2xl border border-white/8 bg-[#121214] p-4">
              <Icon className="w-5 h-5 text-[#D4AF37] mb-2" />
              <div className="font-heading text-2xl text-white">{val}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#71717A]">{label}</div>
            </div>
          ))}
        </div>
      )}

      <PathCreator onDone={load} />

      <div className="space-y-3" data-testid="admin-paths-list">
        {data.paths.map((p) => <PathRow key={p.path_id} path={p} onRefresh={load} />)}
      </div>
    </div>
  );
}
